// @flow
import { logError } from '@rs-core/utils/logger';
import axios from 'axios';

type ClinicalInfo = {
	PatientMRN: string,
	Accession: string,
	PatientName: string,
	PatientBirthDate: string,
	PatientAge: string,
	PatientSex: string,
	Modality: string,
	BodyPartExamined: string,
	StudyDescription: string,
	StudyDate: string,
	StudyTime: string,
	StudyInstanceUID: string,
	ReasonForExam: string,
	KeyFindings: string,
	ReferringPhysician: string,
	ReportingPhysician: string,
	Notes: string,
};

type UserInfo = {
	UserName: string,
	UserEmail: string,
};

type ReportingApp = {
	AppLanguage: string,
	SpeechLanguage: string,
	ReturnFormat: string,
	SyncLaunch: boolean,
};

type ResponseType = {
	type: string,
};

// Add Config type definition
type Config = {
	data_sources: {
		fhir: string,
	},
};

// Add specific response type
type MDAiReportResponse = {
	url: string,
	// add other expected response fields
};

type MDAiReportParams = {
	clinicalInfo: ClinicalInfo,
	userInfo: UserInfo,
	reportingApp: ReportingApp,
	response: ResponseType,
	_config: Config, // Fix underscore consistency
};

type CallbackFn = (data?: any) => void;

export const createMDAiReportLink = async (
	params: MDAiReportParams,
	onSuccess: CallbackFn = () => {},
	onError: CallbackFn = () => {}
): Promise<MDAiReportResponse> => {
	// Add specific validation for mandatory clinicalInfo fields
	if (!params.clinicalInfo.PatientMRN || !params.clinicalInfo.Accession) {
		const error = new Error('Missing mandatory fields: PatientMRN and Accession are required in clinicalInfo');
		logError('createMDAiReportLink', error.message);
		onError(error);
		throw error;
	}

	const data = {
		clinicalInfo: params.clinicalInfo,
		userInfo: params.userInfo,
		reportingApp: params.reportingApp,
		response: params.response,
	};

	try {
		const res = await axios.post<MDAiReportResponse>(
			`${params._config.data_sources.fhir}/MdAI/create-report-url`,
			data
		);

		if (!res?.data) {
			const error = new Error('Invalid response from server');
			logError('createMDAiReportLink', error.message);
			throw error;
		}

		onSuccess(res.data);
		return res.data;
	} catch (error) {
		const errorDetails = error.response
			? {
					status: error.response.status,
					statusText: error.response.statusText,
					data: error.response.data,
			  }
			: { message: error.message };

		const errorMessage = `Error creating MDAi report link: ${JSON.stringify(errorDetails)}`;
		logError('createMDAiReportLink', errorMessage);
		onError(error);
		throw error;
	}
};

export default createMDAiReportLink;
